<template>
	<div class="dashboard_tab report_theme_container" data-theme="dashboard">
		<!-- Header sin chips de período (la vista es siempre mensual) ni botón
			Exportar (D6). El calendario es de RANGO (Steve, 2026-10-06) y
			comparte el estado con los demás tabs: la tabla es la del último
			mes del rango y los gráficos cubren sus meses. -->
		<TabHeader
			title="Dashboard"
			:description="description"
			:endMonth="endMonth"
			:getNow="getNow"
			filter-period="month"
			:filter-options="[]"
			calendar-mode="range"
			:date-range="calendarRange"
			:show-export="false"
			compact
			@update-date-range="updateDateRange">
			<!-- Estado de Resultados / Balance. El modo Balance queda para una
				fase posterior (D3): el botón se ve, deshabilitado. -->
			<template #center-sub-nav>
				<div class="sub_nav_filters" role="group" aria-label="Modo del dashboard">
					<button v-for="mode in modes" :key="mode.id"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeMode === mode.id }"
						:disabled="!mode.enabled"
						:aria-pressed="activeMode === mode.id ? 'true' : 'false'"
						v-tooltip.bottom="mode.enabled ? null : 'Disponible pronto'">
						{{ mode.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Error del fetch: reemplaza el contenido (como en los demás tabs),
			con el reintento a mano. -->
		<div v-if="error && !hasDashboard" class="dashboard_state">
			<p class="dashboard_state_text has-text-danger">{{ error }}</p>
			<button type="button" class="action_btn" @click="retry">Reintentar</button>
		</div>

		<div v-else class="dashboard_layout mt-3">
			<section class="dashboard_table_area">
				<!-- Mes focal sin movimientos del estado de resultados: aviso con el
					salto al último mes CON movimientos del P&L (lo calcula el backend
					sobre su ventana; nunca es el focal, el guard es defensivo). Los
					gráficos siguen mostrando los 12 meses. -->
				<div v-if="focalMonthEmpty" class="dashboard_notice">
					<p>
						El estado de resultados no tiene movimientos en {{ focalMonthLabel }}.
						Elige otro mes o revisa que la información de ese período esté cargada.
					</p>
					<button v-if="canJumpToLastDataMonth" type="button" class="action_btn"
						@click="goToMonth(lastDataMonth)">
						Ir a {{ lastDataMonthLabel }}
					</button>
				</div>
				<!-- Clic en una fila de Ventas, Costos o Gastos (o en una de sus
					cuentas): abre su drawer del mes acá mismo, sin cambiar de tab.
					Las demás filas no son clickeables (D3). -->
				<DashboardSummaryTable
					:month="focalMonthKey"
					:rows="rows"
					:is-loading="isLoading"
					:floor-month="floorMonth"
					:expanded-rows="expandedRows"
					:drill-tabs="drillTabs"
					@toggle-childs="toggleRow"
					@row-click="onRowClick" />
			</section>

			<section class="dashboard_charts_grid">
				<!-- Sólo el título por card (Steve, 2026-10-06): sin subtítulo y
					sin la banda del mes focal en los gráficos. -->
				<DashboardChartCard v-for="card in cards" :key="card.id"
					:title="card.title"
					:is-loading="isLoading"
					:has-data="card.hasData"
					:error="error || ''">
					<!-- Clic en un mes de cualquier gráfico lo vuelve el mes focal:
						la tabla se recalcula. Leyendas compactas; la de Ventas &
						Márgenes lista sólo las líneas (D7) y EBITDA, con una sola
						serie, no lleva. -->
					<KpiComposedChart
						:monthHeaders="monthLabels"
						:series="card.series"
						:group="chartGroup"
						legend-compact
						:legend-lines="card.legendLines"
						:show-legend="card.showLegend"
						@chart-click="onChartClick" />
				</DashboardChartCard>
			</section>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapActions, mapGetters, mapState } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import DashboardSummaryTable from "@/components/financialReports/dashboard/DashboardSummaryTable.vue";
import DashboardChartCard from "@/components/financialReports/dashboard/DashboardChartCard.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import { DASHBOARD_MODE_RESULTADOS } from "@/store/financialReports/dashboardReport";
import { displayMonthRange, resolveDateRange } from "@/store/financialReports/utils/dateRange";
import { DRAWER_TABS, drawerFor } from "@/views/financialReports/utils/dashboardDrawers";
import {
	burnSeries,
	ebitdaSeries,
	hasAnyData,
	salesAndMarginsSeries,
} from "@/views/financialReports/utils/dashboardSeries";

const MODES = Object.freeze([
	{ id: DASHBOARD_MODE_RESULTADOS, label: "Resultados", enabled: true },
	{ id: "balance", label: "Balance", enabled: false },
]);
// Grupo de ECharts: los 3 gráficos muestran el tooltip del mismo mes a la vez.
const CHART_GROUP = "financial-dashboard";
// Las tres cards: Ventas & Márgenes (como el primer gráfico de Rentabilidad),
// Gross burn y EBITDA (observaciones de Steve, 2026-10-06).
const CARDS = Object.freeze([
	{ id: "sales", title: "Ventas & Márgenes", build: salesAndMarginsSeries, legendLines: true, showLegend: true },
	{ id: "burn", title: "Gross burn", build: burnSeries, legendLines: false, showLegend: true },
	{ id: "ebitda", title: "EBITDA", build: ebitdaSeries, legendLines: false, showLegend: false },
]);

const monthLabel = (key) => moment(key, "YYYY-MM").locale("es").format("MMMM YYYY");

// Tab Dashboard (docs/decisiones/37-dashboard-reporte-financiero.md): la tabla resumen
// del mes focal y 3 gráficos de los meses del rango, de UNA respuesta del
// store `dashboardReport`. El rango es el compartido con los demás tabs
// (`reportsFilters`, resuelto como Mensual); el mes focal es su último mes.
export default {
	name: "DashboardTab",

	components: { TabHeader, DashboardSummaryTable, DashboardChartCard, KpiComposedChart },

	data() {
		// Totales con sus cuentas desplegadas (arranca plegado, como el Resumen).
		return { modes: MODES, chartGroup: CHART_GROUP, expandedRows: [], drillTabs: DRAWER_TABS };
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange", "dataFirstMonth"]),
		...mapState("dashboardReport", {
			dashboard: (state) => state.dashboard,
			isLoading: (state) => state.isLoading,
			error: (state) => state.error,
			activeMode: (state) => state.mode,
		}),
		...mapGetters("dashboardReport", ["hasDashboard"]),
		...mapState("classification", ["categories"]),

		getNow() {
			return moment().toDate();
		},
		// Rango del tab resuelto como Mensual (el Dashboard no tiene otra
		// granularidad): el default son los últimos 12 meses hasta `endMonth`.
		range() {
			return resolveDateRange(
				this.endMonth, this.selectedPeriodRange, "month", this.customRange, null, this.dataFirstMonth,
			);
		},
		rangeSignature() {
			return `${this.range.initDate}|${this.range.endDate}`;
		},
		// Lo que pinta el trigger del calendario.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		focalMonthKey() {
			return moment(this.range.endDate).format("YYYY-MM");
		},
		description() {
			const init = monthLabel(moment(this.range.initDate).format("YYYY-MM"));
			return `Resumen de ${this.focalMonthLabel} y evolución entre ${init} y ${this.focalMonthLabel}`;
		},
		focalMonthLabel() {
			return monthLabel(this.focalMonthKey);
		},
		rows() {
			return this.dashboard ? this.dashboard.rows : [];
		},
		floorMonth() {
			return this.dashboard ? this.dashboard.floorMonth : null;
		},
		lastDataMonth() {
			return this.dashboard ? this.dashboard.lastDataMonth : null;
		},
		lastDataMonthLabel() {
			return this.lastDataMonth ? monthLabel(this.lastDataMonth) : "";
		},
		canJumpToLastDataMonth() {
			return Boolean(this.lastDataMonth) && this.lastDataMonth !== this.focalMonthKey;
		},
		// Sin ningún monto en el mes focal (todo 0 o null) no hay qué leer en
		// la tabla; el aviso ofrece saltar al último mes con datos.
		focalMonthEmpty() {
			if (!this.hasDashboard || this.isLoading) return false;
			return this.rows.every((row) => !row.isMoney || !row.values.month);
		},
		series() {
			return this.dashboard ? this.dashboard.series : null;
		},
		monthLabels() {
			return this.series ? this.series.monthLabels : [];
		},
		cards() {
			return CARDS.map(({ id, title, build, legendLines, showLegend }) => {
				const series = this.series ? build(this.series) : [];
				return { id, title, series, legendLines, showLegend, hasData: hasAnyData(series) };
			});
		},
	},

	watch: {
		rangeSignature() {
			this.fetch();
		},
	},

	mounted() {
		this.fetch();
	},

	methods: {
		...mapActions("reportsFilters", ["setCustomRange"]),
		// Categorías + ítems son globales; el store hace short-circuit si ya
		// cargaron por otro view. El drawer de Ventas las usa para agrupar.
		...mapActions("classification", ["getCategories"]),

		fetch() {
			const { initDate, endDate } = this.range;
			this.$store.dispatch("dashboardReport/fetchDashboard", { initDate, endDate });
		},
		retry() {
			this.fetch();
		},
		// El calendario del header emite el rango elegido; la action del store
		// lo guarda y sincroniza `endMonth` con su fin.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// Mover el mes focal (botón «Ir a…», clic en un gráfico): el rango se
		// desplaza entero conservando su largo y termina en ese mes.
		goToMonth(monthKey) {
			const to = moment(monthKey, "YYYY-MM").startOf("month");
			const months = moment(this.range.endDate).diff(moment(this.range.initDate), "months");
			this.setCustomRange({ from: to.clone().subtract(months, "months").toDate(), to: to.toDate() });
		},
		toggleRow(key) {
			this.expandedRows = this.expandedRows.includes(key)
				? this.expandedRows.filter((k) => k !== key)
				: [...this.expandedRows, key];
		},
		// Clic en una fila de Ventas / Costos / Gastos: abre el drawer del mes
		// focal de ese tab acá mismo (Steve, 2026-10-06), con el mismo módulo
		// Vuex y la misma config de modal que usa el tab al consumir su
		// `pendingDrill`. Una cuenta abre el drawer acotado a ella. Cerrar el
		// drawer resetea su módulo; cambiar el mes desde su calendario vuelve
		// a pedir con el mismo recorte.
		async onRowClick(row) {
			const drawer = drawerFor(row, {
				month: this.focalMonthKey, categories: this.categories, getNow: this.getNow,
			});
			if (!drawer) return;
			await this.getCategories();
			const fetchDrawer = (monthKey) => this.$store.dispatch(
				`${drawer.analysisNs}/${drawer.fetchAction}`, drawer.payloadFor(monthKey),
			);
			fetchDrawer(this.focalMonthKey);
			this.$buefy.modal.open({
				...drawer.modalConfig,
				parent: this,
				events: {
					closed: () => this.$store.commit(`${drawer.analysisNs}/RESET`),
					"update-end-month": (date) => fetchDrawer(moment(date).format("YYYY-MM")),
				},
			});
		},
		// Clic en un punto de un gráfico → ese mes pasa a ser el focal (no-op
		// sobre el mes ya elegido).
		onChartClick(params) {
			const months = this.series ? this.series.months : [];
			const monthKey = params && months[params.dataIndex];
			if (!monthKey || monthKey === this.focalMonthKey) return;
			this.goToMonth(monthKey);
		},
	},
};
</script>

<style scoped lang="scss">
.dashboard_tab {
	position: relative;
	// El tab mide COMO MÁXIMO lo que queda de ventana bajo el chrome (mismo
	// tope que el Resumen: 100vh menos el header global y el padding de 30px
	// de `.main_div`). Columna flex: el header ocupa lo suyo y el layout se
	// encoge hasta el resto; adentro, la tabla scrollea sola con su header
	// fijo cuando sus cuentas desplegadas no entran (Steve, 2026-10-07).
	// Con pocas filas mide lo que su contenido: nada se estira.
	display: flex;
	flex-direction: column;
	max-height: calc(100vh - var(--app-chrome-top, 56px) - 30px);
}

// La tabla manda (Steve, 2026-10-07): ocupa el ancho que necesita, sin
// scroll horizontal, y los gráficos van en UNA columna a su derecha con el
// ancho restante, apilados y estirados al alto de la tabla (una card por
// tercio). La pista `auto` toma el ancho intrínseco de la tabla (sus
// columnas fijas) y `1fr` el resto. Bajo 1400 px no entran las dos
// columnas: la tabla pasa arriba y los gráficos a una fila de tres debajo;
// bajo 800 px, uno por fila.
.dashboard_layout {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	// Una fila que puede ENCOGER (`minmax(0, 1fr)`, no `auto`): cuando el tab
	// toca su tope, la fila toma el alto disponible y la tabla scrollea
	// adentro; la columna de gráficos sigue el alto de la fila.
	grid-template-rows: minmax(0, 1fr);
	gap: 16px;
	align-items: stretch;
	flex: 0 1 auto;
	min-height: 0;

	// Apilado (tabla arriba, gráficos abajo) no hay cadena que encoger: las
	// dos filas miden su contenido y la página scrollea, como Rentabilidad.
	@media (max-width: 1399px) {
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto auto;
		flex-shrink: 0;
	}
}

.dashboard_table_area {
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-height: 0;

	// La tabla es lo único que se encoge: el aviso de mes vacío no.
	.dashboard_notice { flex-shrink: 0; }
}

// El contenedor de la tabla scrollea cuando no entra; con pocas filas mide
// lo que su contenido (`flex: 0 1 auto`, sin crecer). El hueco de la barra
// vertical queda reservado siempre (`scrollbar-gutter`): si apareciera recién
// al desplegar, le robaría 15 px a la tabla de ancho fijo y asomaría una
// barra horizontal.
.dashboard_table_area :deep(.summary_table_container) {
	flex: 0 1 auto;
	min-height: 0;
	overflow: auto;
	scrollbar-gutter: stable;
}

// Apilado: la tabla reparte sus columnas en todo el ancho disponible.
@media (max-width: 1399px) {
	.dashboard_table_area :deep(.summary_table) {
		width: 100%;
	}
}

.dashboard_charts_grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	grid-template-rows: repeat(3, minmax(180px, 1fr));
	gap: 12px;
	min-width: 0;
	min-height: 0;

	@media (max-width: 1399px) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		grid-template-rows: none;
		grid-auto-rows: 240px;
	}

	@media (max-width: 800px) {
		grid-template-columns: minmax(0, 1fr);
	}
}

.dashboard_notice {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 16px;
	border: 1px solid var(--report-accent-border);
	background: var(--report-accent-bg);
	color: var(--report-accent-text);
	border-radius: 12px;
	font-size: 13px;

	p { margin: 0; }
}

.dashboard_state {
	padding: 48px 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
}

.dashboard_state_text {
	margin: 0;
	font-size: 14px;
}

.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 13px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	white-space: nowrap;

	&:hover { background: var(--report-surface); }

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}

// Toggle del header inyectado por slot (mismas clases que Ventas/KPIs; los
// estilos scoped de TabHeader no alcanzan el markup del slot).
.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background: var(--report-filter-wash, var(--report-surface));
	}

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover {
			background: var(--report-filter-accent-hover, var(--report-primary-hover));
		}
	}

	&:disabled {
		color: var(--report-text-muted-soft);
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}
</style>
